import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { templatesApi, favoritesApi } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Heart, 
  FileText, 
  Copy, 
  Check,
  Calendar,
  BarChart3,
  Edit3,
  Download
} from 'lucide-react';

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await templatesApi.getById(id);
        setTemplate(response.data);
      } catch (error) {
        console.error('Error fetching template:', error);
        toast.error('Template not found');
        navigate('/templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, navigate]);

  useEffect(() => {
    const checkFavorite = async () => {
      if (user && template) {
        try {
          const response = await favoritesApi.getAll();
          const isFav = response.data.some(f => f.template_id === template.id);
          setIsFavorite(isFav);
        } catch (error) {
          console.error('Error checking favorites:', error);
        }
      }
    };

    checkFavorite();
  }, [user, template]);

// No handleDownload needed - using editor page instead

  const handleToggleFavorite = async () => {
    if (!user) {
      toast.error('Please log in to save favorites');
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await favoritesApi.remove(template.id);
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await favoritesApi.add(template.id);
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(template.full_content);
    setCopied(true);
    toast.success('Content copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-stone-200 rounded w-1/4" />
            <div className="h-12 bg-stone-200 rounded w-3/4" />
            <div className="h-64 bg-stone-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="template-detail-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link 
          to="/templates" 
          className="inline-flex items-center gap-2 text-stone-600 hover:text-primary mb-6 transition-colors"
          data-testid="back-to-templates"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to templates
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <Badge className="bg-primary-50 text-primary border-0 mb-3">
                {template.category}
              </Badge>
              <h1 className="font-serif text-3xl sm:text-4xl font-bold text-primary-900">
                {template.title}
              </h1>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleToggleFavorite}
              className={`${isFavorite ? 'bg-red-50 border-red-200 text-red-500' : 'border-stone-200 text-stone-500'} hover:bg-red-50 hover:border-red-200 hover:text-red-500`}
              data-testid="favorite-btn"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
          </div>
          
          <p className="text-lg text-stone-600 mb-6">{template.description}</p>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-stone-500">
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              <span>{template.download_count.toLocaleString()} downloads</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Updated {new Date(template.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span>{template.sections.length} sections</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="preview" className="w-full">
              <TabsList className="bg-stone-100 mb-6">
                <TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger>
                <TabsTrigger value="sections" data-testid="tab-sections">Sections</TabsTrigger>
              </TabsList>
              
              <TabsContent value="preview">
                <Card className="bg-white border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-stone-900">Full Template</h3>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleCopyContent}
                        className="text-stone-500 hover:text-primary"
                        data-testid="copy-content-btn"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-4 overflow-x-auto">
                      <pre className="template-preview text-stone-700 whitespace-pre-wrap">
                        {template.full_content}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="sections">
                <Card className="bg-white border-stone-200">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-stone-900 mb-4">Template Sections</h3>
                    <p className="text-sm text-stone-600 mb-4">
                      This template includes the following sections:
                    </p>
                    <ul className="space-y-3">
                      {template.sections.map((section, index) => (
                        <li key={index} className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center text-sm font-medium text-primary">
                            {index + 1}
                          </div>
                          <span className="text-stone-700">{section}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Download Card */}
            <Card className="bg-white border-stone-200 sticky top-24">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Download Template</h3>
                <p className="text-sm text-stone-600 mb-6">
                  Choose your preferred format. Both include the full template content.
                </p>
                
                <Button 
                  className="w-full bg-primary hover:bg-primary-800 text-white btn-lift h-12"
                  onClick={() => navigate(`/templates/${id}/edit`)}
                  data-testid="customize-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Customize & Download
                </Button>

                {!user && (
                  <p className="text-xs text-stone-500 mt-4 text-center">
                    <Link to="/login" className="text-primary hover:underline">Log in</Link> to download templates
                  </p>
                )}
                
                {user && (
                  <p className="text-xs text-stone-500 mt-4 text-center">
                    {user.downloads_remaining === -1 
                      ? 'Unlimited downloads with Pro' 
                      : `${user.downloads_remaining} downloads remaining`}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Tags */}
            <Card className="bg-white border-stone-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-stone-900 mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {template.tags.map((tag) => (
                    <Link 
                      key={tag} 
                      to={`/templates?search=${tag}`}
                      data-testid={`tag-${tag}`}
                    >
                      <Badge 
                        variant="secondary" 
                        className="bg-stone-100 text-stone-600 hover:bg-primary-50 hover:text-primary cursor-pointer"
                      >
                        {tag}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
